<template>
    <div class="advertisement-moderation-external">
        <div class="advertisement-moderation-external__header">
            <h1 class="advertisement-moderation-external__title">Модерация группы объявлений: {{ displayName }}</h1>
        </div>
        <p class="advertisement-moderation-external__intro">
            Группа раскрывается на отдельные объявления по одному экрану. Креативы отображаются только для соответствующего oid.
        </p>
        <div class="advertisement-moderation-external__list">
            <div 
                v-for="advertisement in advertisements" 
                :key="advertisement.id" 
                class="moderation-card moderation-card--sub"
            >
                <div class="moderation-card__header">
                    <div class="moderation-card__info">
                        <h3 class="moderation-card__title">
                            <span 
                                v-if="advertisement.api_id" 
                                class="moderation-card__sent-icon" 
                                :title="`Отправлено в API (api_id: ${advertisement.api_id})`"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="12" cy="12" r="10" fill="#10b981"/>
                                    <path d="M8 12.5L11 15.5L16 9.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </span>
                            {{ advertisement.name }}
                        </h3>
                        <div class="moderation-card__meta">
                            <span>ID: {{ advertisement.id }}</span>
                            <span class="moderation-card__status-badge" :class="`moderation-card__status-badge--${advertisement.status}`">
                                {{ statusLabel(advertisement.status) }}
                            </span>
                            <span v-if="advertisement.frame">Экран: {{ advertisement.frame.address }}, {{ advertisement.frame.city }}</span>
                            <span v-if="advertisement.gid" class="muted">GID: {{ advertisement.gid }}</span>
                        </div>
                        <div v-if="advertisement.oid" class="moderation-card__oid">
                            <strong>OID:</strong> {{ advertisement.oid }}
                        </div>
                        
                        <!-- Креативы -->
                        <div class="moderation-card__creatives">
                            <strong>Креативы ({{ advertisement.creatives.length }}):</strong>
                            <div v-if="advertisement.creatives.length > 0" class="creatives-list">
                                <div 
                                    v-for="creative in advertisement.creatives" 
                                    :key="creative.id" 
                                    class="creative-item"
                                >
                                    <div class="creative-preview">
                                        <img 
                                            v-if="creative.type === 'picture' && creative.url" 
                                            :src="creative.url" 
                                            :alt="creative.name"
                                        >
                                        <video 
                                            v-else-if="creative.type === 'video' && creative.url" 
                                            :src="creative.url" 
                                            muted
                                        ></video>
                                        <div v-else class="creative-placeholder">?</div>
                                    </div>
                                    <span class="creative-name">{{ creative.name }}</span>
                                </div>
                            </div>
                            <span v-else class="muted">Нет креативов для этого oid</span>
                        </div>

                        <!-- Время показа -->
                        <div v-if="advertisement.time_settings && advertisement.time_settings.length > 0" class="moderation-card__time-settings">
                            <strong>Время показа:</strong>
                            <div class="time-settings-list">
                                <span v-for="ts in advertisement.time_settings" :key="`${ts.day}-${ts.start_time}`" class="time-setting">
                                    {{ dayName(ts.day) }}: {{ formatTime(ts.start_time) }} - {{ formatTime(ts.end_time) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="moderation-card__actions">
                    <a :href="`/guaranteed/campaigns/${advertisement.campaign_id}/advertisements/${advertisement.id}/moderation`" class="btn btn--primary">
                        Просмотр и редактирование
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AdvertisementModerationExternal',
    props: {
        initialAdvertisements: {
            type: Array,
            required: true
        },
        displayName: {
            type: String,
            default: 'Группа объявлений'
        },
        advertisementExternalId: {
            type: [Number, String],
            required: true
        }
    },
    data() {
        return {
            advertisements: [...this.initialAdvertisements],
        };
    },
    methods: {
        statusLabel(status) {
            const labels = {
                'pending_approval': 'На модерации',
                'approve': 'Approve',
                'review': 'Review',
                'reserved': 'Reserved',
                'active': 'Active',
                'pause': 'Pause',
                'paused': 'Paused',
                'resume': 'Resume',
                'completed': 'Completed',
                'cancel': 'Cancel',
                'cancelled': 'Cancelled',
                'rejected': 'Rejected',
            };
            return labels[status] || status || '—';
        },
        dayName(day) {
            const days = {
                1: 'Пн',
                2: 'Вт',
                3: 'Ср',
                4: 'Чт',
                5: 'Пт',
                6: 'Сб',
                7: 'Вс',
            };
            return days[day] || day;
        },
        formatTime(ms) {
            if (ms === null || ms === undefined) return '—';
            const hours = Math.floor(ms / 3600000);
            const minutes = Math.floor((ms % 3600000) / 60000);
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        },
    }
};
</script>

<style scoped>
.advertisement-moderation-external {
    padding: 24px;
}

.advertisement-moderation-external__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.advertisement-moderation-external__title {
    font-size: 24px;
    font-weight: 600;
    margin: 0;
}

.advertisement-moderation-external__intro {
    color: #64748b;
    margin-bottom: 24px;
}

.moderation-card {
    background: #fff;
    border: 1px solid #e6e9ef;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 16px;
}

.moderation-card__header {
    margin-bottom: 16px;
}

.moderation-card__title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 12px 0;
}

.moderation-card__sent-icon {
    display: inline-flex;
}

.moderation-card__meta {
    display: flex;
    gap: 16px;
    margin-bottom: 8px;
    font-size: 14px;
    color: #666;
    flex-wrap: wrap;
    align-items: center;
}

.moderation-card__oid {
    margin-bottom: 8px;
    font-size: 13px;
    color: #64748b;
}

.moderation-card__creatives {
    margin: 12px 0;
    font-size: 14px;
}

.creatives-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 8px;
}

.creative-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}

.creative-preview {
    width: 80px;
    height: 80px;
    border-radius: 8px;
    overflow: hidden;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
}

.creative-preview img,
.creative-preview video {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.creative-placeholder {
    color: #94a3b8;
    font-size: 24px;
}

.creative-name {
    font-size: 11px;
    color: #64748b;
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: center;
}

.moderation-card__time-settings {
    margin: 12px 0;
    font-size: 14px;
}

.time-settings-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
}

.time-setting {
    background: #f1f5f9;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
}

.moderation-card__actions {
    padding-top: 16px;
    border-top: 1px solid #e6e9ef;
}

.muted {
    color: #999;
}

.moderation-card__status-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    text-transform: capitalize;
}

.moderation-card__status-badge--pending_approval { background: #fef3c7; color: #92400e; }
.moderation-card__status-badge--approve { background: #dbeafe; color: #1e40af; }
.moderation-card__status-badge--review { background: #e0e7ff; color: #3730a3; }
.moderation-card__status-badge--reserved { background: #d1fae5; color: #065f46; }
.moderation-card__status-badge--active { background: #d1fae5; color: #065f46; }
.moderation-card__status-badge--pause,
.moderation-card__status-badge--paused { background: #fef3c7; color: #92400e; }
.moderation-card__status-badge--completed { background: #e5e7eb; color: #374151; }
.moderation-card__status-badge--cancel,
.moderation-card__status-badge--cancelled { background: #fee2e2; color: #991b1b; }
.moderation-card__status-badge--rejected { background: #fee2e2; color: #991b1b; }
</style>
